import React, { Component } from 'react';
import { urlMap } from '../utils';
import { withRouter } from './navigation/withNavigation';
import PomodoroSessionCard from './dashboard/chapterPage/PomodoroSessionCard';
import ProjectProgressView from './components/ProjectProgressView';

function slugToDisplayName(slug) {
  return String(slug || 'author-name')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDuration(secondsInput) {
  const totalSeconds = Math.max(0, Number(secondsInput) || 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function formatSessionDate(value) {
  if (!value) return 'Unknown date';
  return new Date(value).toLocaleString();
}

function formatExcerpt(value, maxCharacters = 64) {
  const text = String(value || '').trim();
  if (text.length <= maxCharacters) return text;
  return `${text.slice(0, maxCharacters).trimEnd()}…`;
}

class PublicAuthorFeed extends Component {
  state = {
    loading: true,
    error: '',
    payload: {
      author: null,
      sessions: [],
      progress: [],
      pagination: { page: 1, pageSize: 5, totalItems: 0, totalPages: 1 },
    },
    expandedSessions: {},
    activeView: 'sessions',
    isMobileFeed: typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches    
  };

  isUnmounted = false;

  componentDidMount() {
    this.mobileFeedQuery = window.matchMedia('(max-width: 760px)');
    this.mobileFeedQuery.addEventListener('change', this.handleMobileFeedChange);
    if (!this.props.previewData) {
      this.loadFeed();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.previewData) return;
    const prevSlug = String(prevProps?.params?.authorSlug || '').trim().toLowerCase();
    const nextSlug = String(this.props?.params?.authorSlug || '').trim().toLowerCase();
    if (prevSlug !== nextSlug) {
      this.loadFeed();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.mobileFeedQuery?.removeEventListener('change', this.handleMobileFeedChange);
  }

  handleMobileFeedChange = (event) => {
    this.setState({ isMobileFeed: event.matches });
  };

  toggleSessionExpanded = (sessionId) => {
    if (!sessionId) return;
    this.setState((prev) => ({
      expandedSessions: {
        ...prev.expandedSessions,
        [sessionId]: !prev.expandedSessions[sessionId],
      },
    }));
  };

  getSafeSlug = () => String(this.props?.params?.authorSlug || '').trim().toLowerCase();  

  loadFeed = async () => {
    const safeSlug = this.getSafeSlug();
    if (!safeSlug) {
      this.setState({ loading: false, error: 'Missing author slug.' });
      return;
    }

    this.setState({ loading: true, error: '' });
    try {
      const res = await fetch(urlMap.publicAuthorFeed(safeSlug, 1, 25));
      if (!res.ok) throw new Error('Failed to load feed');
      const body = await res.json();
      const totalPages = Math.max(1, Number(body?.pagination?.totalPages) || 1);
      const remainingBodies = await Promise.all(
        Array.from({ length: totalPages - 1 }, async (_, index) => {
          const pageRes = await fetch(urlMap.publicAuthorFeed(safeSlug, index + 2, 25));
          if (!pageRes.ok) throw new Error('Failed to load feed');
          return pageRes.json();
        }),
      );
      if (this.isUnmounted) return;

      const sessions = [body, ...remainingBodies].flatMap((pageBody) => (
        Array.isArray(pageBody?.sessions) ? pageBody.sessions : []
      ));

      this.setState({
        payload: {
          author: body?.author || null,
          currentProject: body?.currentProject || null,
          sessions,
          progress: Array.isArray(body?.progress) ? body.progress : [],
          pagination: body?.pagination || { page: 1, pageSize: 25, totalItems: sessions.length, totalPages: 1 },
          totalWritingSeconds: body?.totalWritingSeconds ?? 0,
          reflectionAnswerSeconds: body?.reflectionAnswerSeconds ?? 0,
        },
        expandedSessions: {},
      });
    } catch {
      if (this.isUnmounted) return;
      this.setState({ error: 'Could not load public author feed.' });
    } finally {
      if (!this.isUnmounted) {
        this.setState({ loading: false });
      }
    }
  };

  render() {
    const { loading, error, payload, expandedSessions, activeView, isMobileFeed } = this.state;
    const effectivePayload = this.props.previewData || payload;
    const authorSlug = this.props?.params?.authorSlug;
    const authorName = effectivePayload?.author?.name || slugToDisplayName(authorSlug);
    const sessions = Array.isArray(effectivePayload?.sessions) ? effectivePayload.sessions : [];
    const progress = Array.isArray(effectivePayload?.progress) ? effectivePayload.progress : [];
    const currentProject = effectivePayload?.currentProject || null;
    const pagination = effectivePayload?.pagination || { page: 1, totalPages: 1, totalItems: sessions.length };
    const totalWritingSeconds = Number(effectivePayload?.totalWritingSeconds) || 0;
    const reflectionAnswerSeconds = Number(effectivePayload?.reflectionAnswerSeconds) || 0;
    const issueGroupsMap = sessions.reduce((groups, session) => {
      const reflections = Array.isArray(session?.reflections) ? session.reflections : [];
      const groupedReflections = reflections.reduce((sessionGroups, reflection) => {
        const issueKey = reflection?.issue_id || 'free-style';
        const rows = sessionGroups.get(issueKey) || [];
        rows.push(reflection);
        sessionGroups.set(issueKey, rows);
        return sessionGroups;
      }, new Map());

      if (groupedReflections.size === 0) groupedReflections.set('free-style', []);

      groupedReflections.forEach((issueReflections, issueKey) => {
        const sample = issueReflections[0];
        const existing = groups.get(issueKey) || {
          id: issueKey,
          title: issueKey === 'free-style' ? 'Free style' : (sample?.issue_title || 'Untitled issue'),
          description: issueKey === 'free-style'
            ? 'Writing sessions without an assigned issue.'
            : (sample?.issue_description || 'No description available'),
          sessions: [],
          totalSeconds: 0,
          reflectionCount: 0,
        };
        existing.sessions.push({ session, reflections: issueReflections });
        existing.totalSeconds += Number(session.actual_writing_seconds) || 0;
        existing.reflectionCount += issueReflections.length;
        groups.set(issueKey, existing);
      });
      return groups;
    }, new Map());
    const issueGroups = Array.from(issueGroupsMap.values());

    return (
      <main className="public-feed public-feed--wip" aria-label="Author feed work in progress">
        <header className="public-feed__header">
          <h1>{`${authorName}'s work in progress`}</h1>
          <div className="public-feed__views" role="tablist" aria-label="Author activity views">
            <button type="button" role="tab" aria-selected={activeView === 'sessions'} className={activeView === 'sessions' ? 'is-active' : ''} onClick={() => this.setState({ activeView: 'sessions' })}>
              Writing sessions <span>{pagination.totalItems}</span>
            </button>
            <button type="button" role="tab" aria-selected={activeView === 'progress'} className={activeView === 'progress' ? 'is-active' : ''} onClick={() => this.setState({ activeView: 'progress' })}>
              Progress
            </button>
          </div>
        </header>

        <section className="public-feed__timeline" aria-label={activeView === 'sessions' ? 'Latest writing sessions' : 'Reflection progress'}>
          {(loading && !this.props.previewData) ? (
            <article className="public-feed__tweet public-feed__tweet--current">
              <div className="public-feed__tweet-body">
                <p>Loading latest sessions...</p>
              </div>
            </article>
          ) : error ? (
            <article className="public-feed__tweet public-feed__tweet--current">
              <div className="public-feed__tweet-body">
                <p>{error}</p>
              </div>
            </article>
          ) : activeView === 'progress' ? (
            <ProjectProgressView
              authorSlug={this.getSafeSlug()}
              currentProject={currentProject}
              progress={progress}
              totalWritingSeconds={totalWritingSeconds}
              reflectionAnswerSeconds={reflectionAnswerSeconds}
            />
          ) : sessions.length === 0 ? (
            <article className="public-feed__tweet public-feed__tweet--current">
              <div className="public-feed__tweet-body">
                <p>No writing sessions yet.</p>
              </div>
            </article>
          ) : (
            <>
              <div className="public-feed__issue-groups">
                {issueGroups.map((issue, issueIndex) => (
                  <details key={issue.id} className="public-feed__issue-group" defaultOpen={!isMobileFeed && issueIndex === 0}>
                    <summary className="public-feed__issue-summary">
                      <span className="public-feed__issue-heading">
                        <strong>{issue.title}</strong>
                        <span className="public-feed__issue-description public-feed__issue-description--full">{issue.description}</span>
                        <span className="public-feed__issue-description public-feed__issue-description--excerpt">{formatExcerpt(issue.description)}</span>
                        <span className="public-feed__issue-time">Total time spent · {formatDuration(issue.totalSeconds)}</span>
                      </span>
                      <span className="public-feed__issue-count">
                        {issue.reflectionCount} reflection{issue.reflectionCount === 1 ? '' : 's'}
                      </span>
                      <span className="public-feed__issue-chevron" aria-hidden="true">▸</span>
                    </summary>
                    <ul className="pomodoro-session-list public-feed__issue-sessions">
                      {issue.sessions.map(({ session, reflections: issueReflections }) => (
                        <PomodoroSessionCard
                          key={`${issue.id}-${session.id}`}
                          session={session}
                          reflections={issueReflections}
                          isExpanded={Boolean(expandedSessions[session.id])}
                          onToggleExpanded={(sessionId) => this.toggleSessionExpanded(sessionId)}
                          titleOverride={`Reflection · ${formatSessionDate(issueReflections[0]?.created_at || session.started_at)}`}
                          groupedIssue
                          publicFeed
                        />
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </>
          )}
        </section>
        
      </main>
    );
  }
}

export default withRouter(PublicAuthorFeed);
